import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../utils/api';
import './TierManagement.css';
import {
  Settings, DollarSign, Users, CheckCircle, XCircle, RefreshCw,
  Plus, Edit, Trash2, Crown, Star, Zap, Package, Layers,
  ArrowUpDown, GripVertical, TrendingUp, Percent
} from 'lucide-react';

const TierManagement = () => {
  const [tiers, setTiers] = useState([]);
  const [features, setFeatures] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingTier, setEditingTier] = useState(null);
  const [newTier, setNewTier] = useState({
    tier: '',
    name: '',
    monthlyPrice: 0,
    yearlyPrice: 0,
    description: '',
    features: [],
    maxProducts: null,
    maxVariationAttributes: null,
    maxTeamMembers: null,
    sortOrder: 0,
    serviceChargeRate: 0,
    serviceChargeWhoPays: 'CUSTOMER'
  });

  const fetchTiers = useCallback(async () => {
    try {
      const response = await api.get('/admin/tiers');

      let tiersData = [];
      if (response.status === 'success' && response.message && Array.isArray(response.message)) {
        tiersData = response.message;
      } else if (response.data && Array.isArray(response.data)) {
        tiersData = response.data;
      } else if (response.data?.data && Array.isArray(response.data.data)) {
        tiersData = response.data.data;
      } else if (response.data?.message && Array.isArray(response.data.message)) {
        tiersData = response.data.message;
      }

      // Sort by sortOrder
      tiersData.sort((a, b) => (a.sortOrder || 0) - (b.sortOrder || 0));
      setTiers(tiersData);
    } catch (err) {
      console.error('Error fetching tiers:', err);
      setError('Failed to load tiers: ' + err.message);
      setTiers([]);
    }
  }, []);

  const fetchFeatures = useCallback(async () => {
    try {
      const response = await api.get('/admin/features');
      setFeatures(response.features || []);
    } catch (err) {
      console.error('Error fetching features:', err);
      setFeatures([]);
    }
  }, []);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError('');
      try {
        await Promise.all([fetchTiers(), fetchFeatures()]);
      } catch (err) {
        setError('Failed to load data');
      } finally {
        setLoading(false);
      }
    };
    loadData();
  }, [fetchTiers, fetchFeatures]);

  const handleDeleteTier = async (tierName) => {
    if (!window.confirm(`Are you sure you want to delete the ${tierName} tier? This cannot be undone.`)) {
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      const response = await api.delete(`/admin/tiers/${tierName}`);
      if (response.status === 'success') {
        setSuccess('Tier deleted successfully!');
        fetchTiers();
      } else {
        throw new Error(response.message || 'Failed to delete tier');
      }
    } catch (err) {
      setError(err.message || 'Failed to delete tier');
    } finally {
      setSaving(false);
    }
  };

  const handleEditTier = (tier) => {
    setEditingTier(tier);
    setNewTier({
      tier: tier.tier,
      name: tier.name,
      monthlyPrice: tier.monthlyPrice || 0,
      yearlyPrice: tier.yearlyPrice || 0,
      description: tier.description || '',
      features: [...(tier.features || [])],
      maxProducts: tier.maxProducts ?? null,
      maxVariationAttributes: tier.maxVariationAttributes ?? null,
      maxTeamMembers: tier.maxTeamMembers ?? null,
      sortOrder: tier.sortOrder ?? 0,
      serviceChargeRate: tier.serviceChargeRate ?? 0,
      serviceChargeWhoPays: tier.serviceChargeWhoPays || 'CUSTOMER'
    });
    setShowCreateModal(true);
  };

  const handleSaveTier = async () => {
    if (!newTier.tier || !newTier.name) {
      setError('Tier ID and name are required');
      return;
    }

    setSaving(true);
    setError('');
    setSuccess('');

    try {
      let response;
      const tierData = {
        ...newTier,
        tier: newTier.tier.toUpperCase().replace(/\s+/g, '_'),
        sortOrder: parseInt(newTier.sortOrder) || 0,
        serviceChargeRate: parseFloat(newTier.serviceChargeRate) || 0
      };

      if (editingTier) {
        response = await api.put(`/admin/tiers/${editingTier.tier}`, tierData);
      } else {
        response = await api.post('/admin/tiers', tierData);
      }

      if (response.status === 'success') {
        setSuccess(editingTier ? 'Tier updated successfully!' : 'Tier created successfully!');
        setShowCreateModal(false);
        setEditingTier(null);
        resetForm();
        fetchTiers();
      } else {
        throw new Error(response.message || 'Failed to save tier');
      }
    } catch (err) {
      setError(err.message || 'Failed to save tier');
    } finally {
      setSaving(false);
    }
  };

  const resetForm = () => {
    setNewTier({
      tier: '',
      name: '',
      monthlyPrice: 0,
      yearlyPrice: 0,
      description: '',
      features: [],
      maxProducts: null,
      maxVariationAttributes: null,
      maxTeamMembers: null,
      sortOrder: tiers.length,
      serviceChargeRate: 0,
      serviceChargeWhoPays: 'CUSTOMER'
    });
  };

  const handleCreateTier = () => {
    setEditingTier(null);
    resetForm();
    setShowCreateModal(true);
  };

  const getTierIcon = (tierName) => {
    const name = tierName?.toLowerCase() || '';
    if (name.includes('free')) return <Star className="tm-icon" />;
    if (name.includes('premium')) return <Crown className="tm-icon" />;
    if (name.includes('pro')) return <Zap className="tm-icon" />;
    if (name.includes('enterprise')) return <TrendingUp className="tm-icon" />;
    return <Settings className="tm-icon" />;
  };

  const formatPrice = (price, currency = 'NGN') => {
    if (price === 0 || price === null || price === undefined) return 'Free';
    try {
      return new Intl.NumberFormat('en-NG', {
        style: 'currency',
        currency: currency,
        minimumFractionDigits: 0
      }).format(price);
    } catch {
      return `${currency} ${price.toLocaleString()}`;
    }
  };

  const getEnabledFeaturesCount = (tier) => {
    return features.filter(f => f.enabledFor?.includes(tier.tier)).length;
  };

  if (loading) {
    return (
      <div className="tm-container">
        <div className="tm-loading">
          <div className="tm-spinner"></div>
          <p>Loading tier management...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="tm-container">
      {/* Page Header */}
      <div className="tm-header">
        <div className="tm-header-left">
          <h1 className="tm-title">
            <Layers className="tm-title-icon" />
            Tier Management
          </h1>
          <p className="tm-subtitle">Create and manage subscription tiers with pricing, limits, and hierarchy</p>
        </div>
        <div className="tm-header-actions">
          <button className="tm-btn tm-btn-secondary" onClick={() => { fetchTiers(); fetchFeatures(); }} disabled={saving}>
            <RefreshCw size={16} /> Refresh
          </button>
          <button className="tm-btn tm-btn-primary" onClick={handleCreateTier} disabled={saving}>
            <Plus size={16} /> Create Tier
          </button>
        </div>
      </div>

      {/* Alerts */}
      {error && (
        <div className="tm-alert tm-alert-error">
          <XCircle size={18} />
          <span>{error}</span>
          <button onClick={() => setError('')} className="tm-alert-close">&times;</button>
        </div>
      )}
      {success && (
        <div className="tm-alert tm-alert-success">
          <CheckCircle size={18} />
          <span>{success}</span>
          <button onClick={() => setSuccess('')} className="tm-alert-close">&times;</button>
        </div>
      )}

      {/* Info Banner */}
      <div className="tm-info-banner">
        <ArrowUpDown size={18} />
        <span><strong>Sort Order</strong> determines tier hierarchy. Lower number = lower tier (e.g., FREE=0). Higher number = premium tier.</span>
      </div>

      {/* Tiers Grid */}
      <div className="tm-grid">
        {tiers.map((tier, index) => {
          const featuresCount = getEnabledFeaturesCount(tier);
          return (
            <div key={tier.tier} className="tm-card">
              {/* Sort Order Badge */}
              <div className="tm-card-order">
                <GripVertical size={14} />
                <span>Order: {tier.sortOrder ?? index}</span>
              </div>

              {/* Card Header */}
              <div className="tm-card-header">
                <div className="tm-card-icon">
                  {getTierIcon(tier.tier)}
                </div>
                <div className="tm-card-info">
                  <h3 className="tm-card-name">{tier.name}</h3>
                  <div className="tm-card-id">{tier.tier}</div>
                </div>
                <div className="tm-card-actions">
                  <button className="tm-btn-icon" onClick={() => handleEditTier(tier)} title="Edit">
                    <Edit size={16} />
                  </button>
                  <button className="tm-btn-icon tm-btn-icon-danger" onClick={() => handleDeleteTier(tier.tier)} title="Delete">
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>

              {/* Pricing */}
              <div className="tm-card-pricing">
                <div className="tm-price-main">
                  <span className="tm-price-value">{formatPrice(tier.monthlyPrice)}</span>
                  {tier.monthlyPrice > 0 && <span className="tm-price-period">/month</span>}
                </div>
                {tier.yearlyPrice > 0 && (
                  <div className="tm-price-yearly">
                    {formatPrice(tier.yearlyPrice)}/year
                  </div>
                )}
              </div>

              {/* Description */}
              {tier.description && (
                <p className="tm-card-desc">{tier.description}</p>
              )}

              {/* Service Charge */}
              <div className="tm-card-charge">
                <Percent size={14} />
                <span>Service Charge: {tier.serviceChargeRate || 0}% ({tier.serviceChargeWhoPays || 'CUSTOMER'})</span>
              </div>

              {/* Limits */}
              <div className="tm-card-limits">
                <div className="tm-limit">
                  <Package size={14} />
                  <span>{tier.maxProducts === null ? 'Unlimited' : tier.maxProducts} products</span>
                </div>
                <div className="tm-limit">
                  <Users size={14} />
                  <span>{tier.maxTeamMembers === null ? 'Unlimited' : tier.maxTeamMembers} team members</span>
                </div>
                <div className="tm-limit">
                  <Layers size={14} />
                  <span>{tier.maxVariationAttributes === null ? 'Unlimited' : tier.maxVariationAttributes} variations</span>
                </div>
              </div>

              {/* Features Count */}
              <div className="tm-card-footer">
                <div className="tm-features-badge">
                  <CheckCircle size={14} />
                  {featuresCount} feature{featuresCount !== 1 ? 's' : ''} enabled
                </div>
              </div>
            </div>
          );
        })}

        {/* Empty State */}
        {tiers.length === 0 && (
          <div className="tm-empty">
            <Layers size={48} />
            <h3>No tiers configured</h3>
            <p>Create your first subscription tier to get started</p>
            <button className="tm-btn tm-btn-primary" onClick={handleCreateTier}>
              <Plus size={16} /> Create Tier
            </button>
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <div className="tm-modal-overlay" onClick={() => setShowCreateModal(false)}>
          <div className="tm-modal" onClick={e => e.stopPropagation()}>
            <div className="tm-modal-header">
              <h2>{editingTier ? 'Edit Tier' : 'Create New Tier'}</h2>
              <button className="tm-modal-close" onClick={() => setShowCreateModal(false)}>
                <XCircle size={20} />
              </button>
            </div>

            <form onSubmit={e => { e.preventDefault(); handleSaveTier(); }} className="tm-modal-body">
              {/* Basic Info */}
              <div className="tm-form-section">
                <h3 className="tm-form-section-title">Basic Information</h3>
                <div className="tm-form-row">
                  <div className="tm-form-group">
                    <label>Tier ID <span className="tm-required">*</span></label>
                    <input
                      type="text"
                      value={newTier.tier}
                      onChange={e => setNewTier({...newTier, tier: e.target.value.toUpperCase().replace(/\s+/g, '_')})}
                      placeholder="e.g., PRO_PLUS"
                      disabled={!!editingTier}
                      required
                    />
                    <span className="tm-form-hint">Unique identifier (uppercase, no spaces)</span>
                  </div>
                  <div className="tm-form-group">
                    <label>Display Name <span className="tm-required">*</span></label>
                    <input
                      type="text"
                      value={newTier.name}
                      onChange={e => setNewTier({...newTier, name: e.target.value})}
                      placeholder="e.g., Pro Plus"
                      required
                    />
                  </div>
                </div>

                <div className="tm-form-group">
                  <label>Description</label>
                  <textarea
                    value={newTier.description}
                    onChange={e => setNewTier({...newTier, description: e.target.value})}
                    placeholder="Describe what this tier offers..."
                    rows="2"
                  />
                </div>
              </div>

              {/* Hierarchy */}
              <div className="tm-form-section">
                <h3 className="tm-form-section-title">
                  <ArrowUpDown size={16} />
                  Tier Hierarchy
                </h3>
                <div className="tm-form-group">
                  <label>Sort Order <span className="tm-required">*</span></label>
                  <input
                    type="number"
                    value={newTier.sortOrder}
                    onChange={e => setNewTier({...newTier, sortOrder: parseInt(e.target.value) || 0})}
                    min="0"
                    required
                  />
                  <span className="tm-form-hint">
                    Lower = lower tier (FREE=0), Higher = premium tier. Current tiers: {tiers.map(t => `${t.name}=${t.sortOrder}`).join(', ')}
                  </span>
                </div>
              </div>

              {/* Pricing */}
              <div className="tm-form-section">
                <h3 className="tm-form-section-title">
                  <DollarSign size={16} />
                  Pricing
                </h3>
                <div className="tm-form-row">
                  <div className="tm-form-group">
                    <label>Monthly Price (NGN)</label>
                    <input
                      type="number"
                      value={newTier.monthlyPrice}
                      onChange={e => setNewTier({...newTier, monthlyPrice: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="0.01"
                    />
                  </div>
                  <div className="tm-form-group">
                    <label>Yearly Price (NGN)</label>
                    <input
                      type="number"
                      value={newTier.yearlyPrice}
                      onChange={e => setNewTier({...newTier, yearlyPrice: parseFloat(e.target.value) || 0})}
                      min="0"
                      step="0.01"
                    />
                  </div>
                </div>
              </div>

              {/* Service Charge */}
              <div className="tm-form-section">
                <h3 className="tm-form-section-title">
                  <Percent size={16} />
                  Service Charge
                </h3>
                <div className="tm-form-row">
                  <div className="tm-form-group">
                    <label>Service Charge Rate (%)</label>
                    <input
                      type="number"
                      value={newTier.serviceChargeRate}
                      onChange={e => setNewTier({...newTier, serviceChargeRate: parseFloat(e.target.value) || 0})}
                      min="0"
                      max="100"
                      step="0.1"
                    />
                  </div>
                  <div className="tm-form-group">
                    <label>Who Pays</label>
                    <select
                      value={newTier.serviceChargeWhoPays}
                      onChange={e => setNewTier({...newTier, serviceChargeWhoPays: e.target.value})}
                    >
                      <option value="CUSTOMER">Customer</option>
                      <option value="MERCHANT">Merchant</option>
                      <option value="SPLIT">Split</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Limits */}
              <div className="tm-form-section">
                <h3 className="tm-form-section-title">
                  <Package size={16} />
                  Limits
                </h3>
                <div className="tm-form-row tm-form-row-3">
                  <div className="tm-form-group">
                    <label>Max Products</label>
                    <input
                      type="number"
                      value={newTier.maxProducts === null ? '' : newTier.maxProducts}
                      onChange={e => setNewTier({...newTier, maxProducts: e.target.value === '' ? null : parseInt(e.target.value) || 0})}
                      min="0"
                      placeholder="Unlimited"
                    />
                  </div>
                  <div className="tm-form-group">
                    <label>Max Team Members</label>
                    <input
                      type="number"
                      value={newTier.maxTeamMembers === null ? '' : newTier.maxTeamMembers}
                      onChange={e => setNewTier({...newTier, maxTeamMembers: e.target.value === '' ? null : parseInt(e.target.value) || 0})}
                      min="0"
                      placeholder="Unlimited"
                    />
                  </div>
                  <div className="tm-form-group">
                    <label>Max Variations</label>
                    <input
                      type="number"
                      value={newTier.maxVariationAttributes === null ? '' : newTier.maxVariationAttributes}
                      onChange={e => setNewTier({...newTier, maxVariationAttributes: e.target.value === '' ? null : parseInt(e.target.value) || 0})}
                      min="0"
                      placeholder="Unlimited"
                    />
                  </div>
                </div>
                <span className="tm-form-hint">Leave empty for unlimited</span>
              </div>

              {/* Actions */}
              <div className="tm-modal-actions">
                <button type="button" className="tm-btn tm-btn-secondary" onClick={() => setShowCreateModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="tm-btn tm-btn-primary" disabled={saving}>
                  {saving ? 'Saving...' : (editingTier ? 'Update Tier' : 'Create Tier')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TierManagement;
